import { z } from 'zod';
import { COMMON_PASSWORD_WORDS, COMMON_PASSWORDS } from './common-passwords';

/**
 * Password policy (D-008): 10–256 Unicode code points and not a common password. Pure and
 * runtime-agnostic, so the web app can show every reason inline before submitting; the API is the
 * one that enforces it (`422 password_policy_violation` with `details.reasons`). The policy is not
 * re-checked at login.
 */

export const PASSWORD_MIN_LENGTH = 10;
export const PASSWORD_MAX_LENGTH = 256;

export const PASSWORD_POLICY_REASONS = ['too_short', 'too_long', 'common_password'] as const;
export type PasswordPolicyReason = (typeof PASSWORD_POLICY_REASONS)[number];
export const passwordPolicyReasonSchema = z.enum(PASSWORD_POLICY_REASONS);

export type PasswordPolicyResult =
  { ok: true; reasons: [] } | { ok: false; reasons: PasswordPolicyReason[] };

/** Personal words that must not be the whole password either (email local part, name). */
export interface PasswordPolicyContext {
  email?: string | null;
  fullName?: string | null;
}

const COMMON_SET = new Set(COMMON_PASSWORDS);
const WORD_SET = new Set(COMMON_PASSWORD_WORDS);

/** Sequences whose runs make weak passwords ("1234567890", "qwertyuiop", "zyxwvu" …). */
const SEQUENCES = [
  '0123456789',
  'abcdefghijklmnopqrstuvwxyz',
  'qwertyuiop',
  'asdfghjkl',
  'zxcvbnm',
  '1qaz2wsx3edc4rfv5tgb6yhn7ujm8ik9ol0p',
  '1q2w3e4r5t6y7u8i9o0p',
  'qazwsxedcrfvtgbyhnujmikolp',
];

const LEET: Record<string, string> = {
  '@': 'a',
  '4': 'a',
  '8': 'b',
  '(': 'c',
  '3': 'e',
  '6': 'g',
  '9': 'g',
  '#': 'h',
  '1': 'i',
  '!': 'i',
  '|': 'l',
  '0': 'o',
  $: 's',
  '5': 's',
  '7': 't',
  '+': 't',
  '2': 'z',
};

/** Digits and symbols people pad words with: "Welcome@2026", "!!Password123". */
const PADDING = /^[^\p{L}]+|[^\p{L}]+$/gu;

function isSequenceRun(value: string): boolean {
  if (value.length < 4) return false;
  return SEQUENCES.some((sequence) => {
    const cyclic = sequence + sequence;
    const reversed = [...cyclic].reverse().join('');
    return cyclic.includes(value) || reversed.includes(value);
  });
}

/** "abcabcabca", "1212121212", "aaaaaaaaaa": one short unit repeated. */
function isRepetition(value: string): boolean {
  return /^(.{1,5})\1+.{0,4}$/su.test(value) && new Set(value).size <= 5;
}

function deLeet(value: string): string {
  return [...value].map((ch) => LEET[ch] ?? ch).join('');
}

function personalWords(context: PasswordPolicyContext): string[] {
  const words: string[] = [];
  const local = context.email?.split('@')[0]?.toLowerCase();
  if (local) {
    words.push(local.replace(/[^\p{L}\p{N}]/gu, ''));
    words.push(...local.split(/[^\p{L}\p{N}]+/u));
  }
  if (context.fullName) {
    const name = context.fullName.toLowerCase();
    words.push(name.replace(/[^\p{L}\p{N}]/gu, ''));
    words.push(...name.split(/[^\p{L}\p{N}]+/u));
  }
  return words.filter((w) => w.length >= 3);
}

/** True when the password is, or trivially derives from, a common password or a personal word. */
export function isCommonPassword(password: string, context: PasswordPolicyContext = {}): boolean {
  const lower = password.normalize('NFKC').toLowerCase();
  if (COMMON_SET.has(lower)) return true;

  const compact = lower.replace(/\s+/gu, '');
  if (isSequenceRun(compact) || isRepetition(compact)) return true;

  // Strip padding, undo substitutions, keep letters: "P@ssw0rd123!" → "password".
  const core = lower.replace(PADDING, '');
  const candidates = new Set([core, deLeet(core), deLeet(core).replace(/[^\p{L}]/gu, '')]);
  // "1" is also a common stand-in for "l": "He11o" → "hello".
  candidates.add(deLeet(core.replaceAll('1', 'l')).replace(/[^\p{L}]/gu, ''));

  const personal = new Set(personalWords(context));
  for (const candidate of candidates) {
    if (candidate.length === 0) continue;
    if (WORD_SET.has(candidate) || COMMON_SET.has(candidate) || personal.has(candidate)) {
      return true;
    }
    if (isSequenceRun(candidate) || isRepetition(candidate)) return true;
  }
  return false;
}

/** Number of Unicode code points (what the 10–256 limits count). */
export function passwordLength(password: string): number {
  return [...password].length;
}

/** Checks every rule and returns all failing reasons at once. */
export function checkPasswordPolicy(
  password: string,
  context: PasswordPolicyContext = {},
): PasswordPolicyResult {
  const reasons: PasswordPolicyReason[] = [];
  const length = passwordLength(password);
  if (length < PASSWORD_MIN_LENGTH) reasons.push('too_short');
  if (length > PASSWORD_MAX_LENGTH) reasons.push('too_long');
  if (length <= PASSWORD_MAX_LENGTH && isCommonPassword(password, context)) {
    reasons.push('common_password');
  }
  return reasons.length === 0 ? { ok: true, reasons: [] } : { ok: false, reasons };
}

/**
 * argon2id parameters (D-008: OWASP m = 19 MiB, t = 2, p = 1). Shared so the API's password
 * service and the seed hash identically. `memoryCost` is in KiB.
 */
export const ARGON2ID_PARAMS = Object.freeze({ memoryCost: 19_456, timeCost: 2, parallelism: 1 });
